import { useCallback, useState } from 'react';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { H2 } from 'foundations-components/transitional/components/H2';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { Form } from 'foundations-components/transitional/components/Form';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { Button } from 'foundations-components/transitional/components/Button';
import ReorderActivityButtonsSelects from './ReorderActivityButtonsSelects';
import FormattedMessage from 'I18n/components/FormattedMessage';
import CrmLogger from 'customer-data-tracking/loggers/CrmLogger';
import { ACTIVITY_CREATOR_REORDER_INTERACTIONS } from 'customer-data-tracking/constants/eventNames';
import { denormalizeTypeId } from 'customer-data-objects/constants/ObjectTypeIds';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
// Returns a very small map of maps of the structure:
// {[activityType]: {[index]: true, [index]: true, ...}, ...}
// Thereby storing a map of duplicated values and the indices of where those
//  duplicates are. This is necessary so that we can:
// - determine what the duplicated value is and where/how many times it's been
//   duplicated
// - un-mark existing duplicates once they've been corrected
// - calculate new duplicates only on save (that way the error messages
//   don't appear while the user is trying to correct their mistakes)
const findIndicesOfDuplicates = selections => {
  const indicesOfDuplicates = selections.reduce((acc, selection, index) => {
    const indices = acc.get(selection);
    if (!indices) {
      const indicesMap = new Map();
      return acc.set(selection, indicesMap.set(index, true));
    }
    return acc.set(selection, indices.set(index, true));
  }, new Map());
  const filteredIndicesOfDuplicates = new Map(Array.from(indicesOfDuplicates.entries()).filter(([__key, value]) => value.size > 1));
  return filteredIndicesOfDuplicates;
};
/** Helper function to delete the nested key and property if found */
function deleteByIndexInMap(originalMap, type, index) {
  const cloneMap = new Map(originalMap);
  if (!cloneMap || cloneMap.size < 1) return cloneMap;
  if (cloneMap.has(type) && cloneMap.get(type).has(index)) {
    cloneMap.get(type).delete(index);
  }
  return cloneMap;
}
function deleteByIndexInArray(originalArray, index) {
  return originalArray.filter((__value, idx) => idx !== index);
}
function addValueByIndex(originalArray, value, index) {
  const cloneArray = [...originalArray];
  cloneArray[index] = value;
  return cloneArray;
}
function compareArrays(arr1, arr2) {
  return arr1.length === arr2.length && arr1.every((ele, idx) => ele === arr2[idx]);
}
const ReorderActivityButtonsModal = ({
  allActivityButtons,
  objectTypeId,
  onCancel,
  onSave,
  selectedActivityButtons
}) => {
  const [orderedSelectedActivityButtons, setOrderedSelectedActivityButtons] = useState(selectedActivityButtons);
  const [indicesOfDuplicates, setIndicesOfDuplicates] = useState(new Map());
  const [hasSaveFailed, setHasSaveFailed] = useState(false);
  const handleSelectedActivitiesChange = useCallback((newSelection, index, previousSelection) => {
    if (!newSelection && !previousSelection || newSelection === previousSelection) {
      // do nothing
    } else if (!newSelection) {
      // if set to empty, delete the selection.
      // a new value set to empty cannot be a duplicate, so delete from duplicates.
      const updatedMap = deleteByIndexInMap(indicesOfDuplicates, previousSelection, index);
      setIndicesOfDuplicates(updatedMap);
      setOrderedSelectedActivityButtons(deleteByIndexInArray(orderedSelectedActivityButtons, index));
    } else if (!previousSelection) {
      // if setting empty to a value, add a brand new value to the selections.
      // if duplicate, it won't be indicated until save
      const updatedArray = [...orderedSelectedActivityButtons, newSelection];
      setOrderedSelectedActivityButtons(updatedArray);
    } else {
      // replace an existing selected value.
      // stop marking index as duplicate since it's been set to new value
      // if the new value is also a duplicate, that'll be calculated on next save.
      if (indicesOfDuplicates.size > 0) {
        const updatedMap = deleteByIndexInMap(indicesOfDuplicates, previousSelection, index);
        setIndicesOfDuplicates(updatedMap);
      }
      const updatedArray = addValueByIndex(orderedSelectedActivityButtons, newSelection, index);
      setOrderedSelectedActivityButtons(updatedArray);
    }
  }, [orderedSelectedActivityButtons, setOrderedSelectedActivityButtons, indicesOfDuplicates, setIndicesOfDuplicates]);
  const handleSave = useCallback(() => {
    const diff = compareArrays(orderedSelectedActivityButtons, selectedActivityButtons);
    // if nothing changed, close select rather than save
    if (diff) {
      return onCancel();
    }
    const duplicates = findIndicesOfDuplicates(orderedSelectedActivityButtons);
    const hasDuplicates = !!duplicates.size;
    const activityButtonsTrackingObject = orderedSelectedActivityButtons.reduce((acc, activity, index) => {
      acc[`activity${index + 1}`] = activity;
      return acc;
    }, {});
    const objectType = denormalizeTypeId(objectTypeId);
    CrmLogger.log(ACTIVITY_CREATOR_REORDER_INTERACTIONS, Object.assign({
      action: 'Save activity button order',
      objectType
    }, activityButtonsTrackingObject, {
      validationError: hasDuplicates
    }));
    if (hasDuplicates) {
      setHasSaveFailed(true);
      return setIndicesOfDuplicates(duplicates);
    }
    return onSave(orderedSelectedActivityButtons);
  }, [objectTypeId, onCancel, onSave, orderedSelectedActivityButtons, selectedActivityButtons, setHasSaveFailed, setIndicesOfDuplicates]);
  return /*#__PURE__*/_jsx(Modal, {
    "data-selenium-test": "reorder-activity-buttons-modal",
    children: /*#__PURE__*/_jsxs(Form, {
      children: [/*#__PURE__*/_jsxs(DialogHeader, {
        children: [/*#__PURE__*/_jsx(H2, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "reorderActivityButtonsModal.title"
          })
        }), /*#__PURE__*/_jsx(DialogCloseButton, {
          onClick: onCancel
        })]
      }), /*#__PURE__*/_jsx(DialogBody, {
        "data-unit-test": "modal-dialog-body",
        paddingBlockStart: "400",
        paddingInlineStart: "500",
        children: /*#__PURE__*/_jsx(View, {
          paddingInlineStart: "500",
          children: /*#__PURE__*/_jsx(ReorderActivityButtonsSelects, {
            allActivityButtons: allActivityButtons,
            orderedSelectedActivityButtons: orderedSelectedActivityButtons,
            onSelectedActivitiesChange: handleSelectedActivitiesChange,
            shouldShowErrors: hasSaveFailed,
            indicesOfDuplicates: indicesOfDuplicates
          })
        })
      }), /*#__PURE__*/_jsxs(DialogFooter, {
        paddingInlineStart: "1000",
        children: [/*#__PURE__*/_jsx(Button, {
          use: "primary",
          "data-unit-test": "modal-save-button",
          "data-selenium-test": "activity-buttons-modal-save-button",
          type: "button",
          onClick: handleSave,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "reorderActivityButtonsModal.saveButton"
          })
        }), /*#__PURE__*/_jsx(Button, {
          use: "tertiary-light",
          "data-selenium-test": "activity-buttons-modal-cancel-button",
          "data-unit-test": "modal-cancel-button",
          type: "button",
          onClick: onCancel,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "reorderActivityButtonsModal.cancelButton"
          })
        })]
      })]
    })
  });
};
export default ReorderActivityButtonsModal;